import { useEffect, useRef } from 'react';
import { useGameState, useGameDispatch } from '../GameProvider';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import { useTimer } from '../hooks/useTimer';
import { resetArrangement, setViolationRows, setAutoWinHand } from '../gameActions';
import { getViolationRows, checkSpecialHand, getHandName5, getHandName3, evaluate5, evaluate3, sortHandByRank, sortHandBySuit, sortHandByPairs } from '../../gameEngine';
import { BOTTOM_SIZE, MIDDLE_SIZE, TOP_SIZE } from '../../gameConfig';
import Card from '../components/Card';
import CardSlot from '../components/CardSlot';
import NemPanel from '../components/NemPanel';
import Timer from '../components/Timer';
import './ArrangingScreen.css';

export default function ArrangingScreen({
  onReady,
  onUnready,
  onNemConfirm,
  onAutoNem,
  onAutoNemSelf,
  onAutoFillAndReady,
  onShowRules,
  onSortHand,
}) {
  const state = useGameState();
  const dispatch = useGameDispatch();
  const { handleDropOnSlot, handleDropOnStaging, handleTapSlot } = useDragAndDrop();

  const {
    myHand, arrangement, originalHand, violationRows,
    readyStatus, uid, dealerUid, gameMode, activeRules,
    timerStartTime, timerDuration,
    nemCompleted, nemDiscardedCards, nemNewCardIds, nemPendingDiscardIds,
    selectedCard,
  } = state;

  // Filter staging to hide cards pending in discard zone
  const visibleStaging = myHand.filter(c => !nemPendingDiscardIds.has(c.id));

  const isNemMode = gameMode === 'nem' || gameMode === 'nemchi';
  const isDealer = uid === dealerUid;
  const discardCount = activeRules.discardCount || 1;
  const firebaseNemDone = !!nemCompleted[uid];
  const localNemDone = nemDiscardedCards.length > 0;
  const myNemDone = firebaseNemDone || localNemDone;
  const amReady = !!readyStatus[uid];

  const nemPendingCards = myHand.filter(c => nemPendingDiscardIds.has(c.id));

  const { remaining } = useTimer(timerStartTime, timerDuration, null);

  const prevRemainingRef = useRef(remaining);
  const expiryHandledRef = useRef(false);

  useEffect(() => {
    expiryHandledRef.current = false;
    prevRemainingRef.current = remaining;
  }, [timerStartTime]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (remaining === 0 && prevRemainingRef.current > 0 && !expiryHandledRef.current && timerStartTime) {
      expiryHandledRef.current = true;
      (async () => {
        if (isNemMode && !myNemDone) {
          if (isDealer) { await onAutoNem?.(); }
          else { await onAutoNemSelf?.(); }
        } else {
          onAutoFillAndReady?.();
        }
      })();
    }
    prevRemainingRef.current = remaining;
  }, [remaining, isNemMode, myNemDone, isDealer, onAutoNem, onAutoNemSelf, onAutoFillAndReady, timerStartTime]);

  useEffect(() => {
    const rows = getViolationRows(arrangement);
    dispatch(setViolationRows(rows));
  }, [arrangement, dispatch]);

  useEffect(() => {
    if (originalHand.length === 13) {
      const special = checkSpecialHand(originalHand);
      dispatch(setAutoWinHand(special));
    }
  }, [originalHand, dispatch]);

  const isFullySet = arrangement.bottom.length === BOTTOM_SIZE &&
    arrangement.middle.length === MIDDLE_SIZE &&
    arrangement.top.length === TOP_SIZE;
  const hasViolations = violationRows.length > 0 && activeRules.violationHelp;
  const canReady = isFullySet && !hasViolations && (!isNemMode || myNemDone);

  const getRowLabel = (row, size) => {
    if (row.length !== size) return '';
    const eval_ = size === 3 ? evaluate3(row) : evaluate5(row);
    return size === 3 ? getHandName3(eval_) : getHandName5(eval_);
  };

  const handleReset = () => {
    dispatch(resetArrangement());
  };

  const handleSort = (type) => {
    const sorters = { rank: sortHandByRank, suit: sortHandBySuit, pairs: sortHandByPairs };
    const sorted = sorters[type]?.(myHand) || myHand;
    onSortHand(sorted);
  };

  const handleStagingDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleStagingDrop = (e) => {
    e.preventDefault();
    try {
      const card = JSON.parse(e.dataTransfer.getData('text/plain'));
      handleDropOnStaging(card);
    } catch { /* ignore */ }
  };

  // Tap staging area background (not a card) — deselect or return row card
  const handleStagingAreaTap = (e) => {
    if (e.target !== e.currentTarget) return; // only background taps
    if (selectedCard?.source === 'row') {
      dispatch({ type: 'RETURN_ROW_CARD_TO_STAGING', row: selectedCard.row, index: selectedCard.index });
    } else {
      dispatch({ type: 'SET_SELECTED_CARD', card: null });
    }
  };

  // Unlimited hand timer (arrangeTimerSeconds = 0): no timerStartTime is
  // written on deal, so hide the countdown entirely (expiry already can't fire).
  const timerActive = !!timerStartTime;

  if (amReady) {
    return (
      <div className="arranging-screen arranging-screen--ready">
        <div className="arranging-screen__header">
          <span className="arranging-screen__status">Ready — Waiting for others...</span>
          {timerActive && <Timer remaining={remaining} />}
        </div>
        <button className="btn btn--secondary" onClick={onUnready}>Edit Hand</button>
      </div>
    );
  }

  const rowDefs = [
    { key: 'top',    label: 'TOP',    size: TOP_SIZE },
    { key: 'middle', label: 'MID',    size: MIDDLE_SIZE },
    { key: 'bottom', label: 'BOT',    size: BOTTOM_SIZE },
  ];

  // Nem sidebar
  const nemSidebar = isNemMode ? (
    myNemDone ? (
      <div className="arranging-sidebar__nem arranging-sidebar__nem--done">
        <span className="arranging-sidebar__nem-label">✓ NEM</span>
        <div className="arranging-sidebar__discarded-cards">
          {nemDiscardedCards.map(card => (
            <Card key={card.id} card={card} isDiscarded size="small" disabled />
          ))}
        </div>
      </div>
    ) : (
      <NemPanel
        discardCount={discardCount}
        pendingCards={nemPendingCards}
        onConfirmNem={onNemConfirm}
        onPendingAdd={(cardId) => dispatch({ type: 'NEM_PENDING_ADD', cardId })}
        onPendingRemove={(cardId) => dispatch({ type: 'NEM_PENDING_REMOVE', cardId })}
      />
    )
  ) : (
    <div className="arranging-sidebar__nem arranging-sidebar__nem--inactive">
      <span className="arranging-sidebar__nem-label">NEM</span>
      <div className="arranging-sidebar__nem-slot" />
    </div>
  );

  return (
    <div className="arranging-screen">

      {/* ── LEFT SIDEBAR ── */}
      <div className="arranging-sidebar">
        {nemSidebar}

        <div className="arranging-sidebar__sorts">
          <button className="btn-sort" onClick={() => handleSort('rank')}  title="Sort by Rank">A↑</button>
          <button className="btn-sort" onClick={() => handleSort('suit')}  title="Sort by Suit">♠</button>
          <button className="btn-sort" onClick={() => handleSort('pairs')} title="Sort by Pairs">2×</button>
        </div>

        <button
          className="btn btn--primary arranging-sidebar__ready-btn"
          disabled={!canReady}
          onClick={onReady}
        >
          READY
        </button>
      </div>

      {/* ── RIGHT MAIN ── */}
      <div className="arranging-main">

        {/* Top bar */}
        <div className="arranging-screen__header">
          <span className="arranging-screen__title">ARRANGE</span>
          <div className="arranging-screen__header-right">
            {hasViolations && isFullySet && (
              <span className="arranging-screen__violation-inline">FOULED</span>
            )}
            {timerActive && <Timer remaining={remaining} />}
            <button className="arranging-screen__rules-btn" onClick={onShowRules}>&#9776;</button>
            <button className="arranging-main__reset" onClick={handleReset} title="Reset all cards to hand">↺</button>
          </div>
        </div>

        {/* Hand (staging) — flush to top, no border box */}
        <div
          className={`arranging-screen__staging${selectedCard?.source === 'row' ? ' arranging-screen__staging--drop-target' : ''}`}
          onDragOver={handleStagingDragOver}
          onDrop={handleStagingDrop}
          onClick={handleStagingAreaTap}
        >
          {visibleStaging.map(card => {
            const isStagingSel = selectedCard?.source === 'staging' && selectedCard.card.id === card.id;
            return (
              <Card
                key={card.id}
                card={card}
                selected={isStagingSel}
                isNemNew={nemNewCardIds.has(card.id)}
                onTap={() => {
                  // Nem first: tap fills the discard zone until discardCount is met…
                  if (isNemMode && !myNemDone && nemPendingDiscardIds.size < discardCount) {
                    dispatch({ type: 'NEM_PENDING_ADD', cardId: card.id });
                  } else {
                    // …then tap auto-places into bottom → middle → top.
                    dispatch({ type: 'AUTO_PLACE_CARD', card });
                  }
                }}
              />
            );
          })}
        </div>

        {/* Three arrangement rows */}
        <div className="arranging-screen__rows">
          {rowDefs.map(({ key, label, size }) => {
            const hasViol = violationRows.includes(key);
            return (
              <div key={key} className="arranging-screen__row">
                <span className="arranging-screen__row-label">{label}</span>
                <div className="arranging-screen__row-slots">
                  {Array.from({ length: size }).map((_, i) => {
                    const card = arrangement[key][i];
                    const isRowCardSel = selectedCard?.source === 'row' &&
                      selectedCard.row === key && selectedCard.index === i;
                    const isSlotHighlighted = !!selectedCard && !card;
                    return (
                      <CardSlot
                        key={`${key}-${i}`}
                        row={key}
                        index={i}
                        onDrop={handleDropOnSlot}
                        onTap={handleTapSlot}
                        violation={hasViol}
                        highlighted={isSlotHighlighted}
                      >
                        {card && (
                          <Card
                            card={card}
                            selected={isRowCardSel}
                            isNemNew={nemNewCardIds.has(card.id)}
                            onTap={() => dispatch({ type: 'MOVE_CARD_TO_STAGING', card })}
                          />
                        )}
                      </CardSlot>
                    );
                  })}
                </div>
                <span className="arranging-screen__row-hand-name">
                  {getRowLabel(arrangement[key], size)}
                </span>
                {arrangement[key].length === size && !hasViol && (
                  <span className="arranging-screen__row-check">✓</span>
                )}
              </div>
            );
          })}
        </div>

      </div>{/* /arranging-main */}
    </div>
  );
}
